/**
 * ProductTileV2 custom element — responsive PLP card (AAS-924).
 *
 * Handles:
 *  - Mobile image carousel → progress bar follows native scroll-snap.
 *  - Mobile overlay reveal → badges + compare/wishlist fade in together a beat
 *    after the page settles, then duck out for the length of a gesture: a
 *    carousel swipe ducks that one card, a page scroll ducks all of them.
 *  - Colour / picture-variant swatch click → selects the colour AND swaps the
 *    card's primary/secondary images to that variant's image pair (overview[0]
 *    + overview[1]). Desktop warms those URLs on hover, then crossfades when
 *    the file is already decoded; otherwise the card commits immediately so a
 *    slow CDN cannot freeze the previous colour for several seconds.
 *  - Size button click → select size.
 *  - Cart "+" button → submits the embedded <product-form> with the resolved
 *    variant id; opens quick-view when a required option is missing (or always
 *    on mobile, where variant UI is hidden).
 *
 * Hover (image crossfade, panel growth, colour/size reveal, action buttons) is
 * pure CSS — see assets/product-tile-v2.css. Cards ship with
 * product-tile-v2--hover-blocked so :hover cannot fade a lazy primary to
 * opacity 0 before it has loaded. JS removes that class (and aria-busy) once
 * the page and primary image are ready. Pointer over the panel suppresses the
 * secondary image so colour selection always shows overview[0]; photo hover
 * still shows that colour's overview[1].
 */

if (!customElements.get('product-tile-v2')) {
  const mqDesktop = window.matchMedia('(min-width: 990px)');
  const mqMobile = window.matchMedia('(max-width: 989px)');
  const mqReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------------------
     Height lock (desktop only)
     ---------------------------------------------------------------------------
     The colour/size reveal lives in normal flow, so on hover it would grow the
     CSS-grid row (grid auto-rows size to max-content). To keep the card height
     FIXED — so the reveal is absorbed by the media shrinking and the content
     flows UP into the image instead of pushing the row down — we lock every
     tile to its rest (row-stretched) height. Recomputed on load, resize, fonts,
     and whenever tiles are added (load-more).
     --------------------------------------------------------------------------- */
  const lockedTiles = new Set();
  let lockRaf = null;

  /* Hover is blocked in Liquid until the page has settled and this card's
     primary image has loaded. CSS :hover fades the (lazy) primary to opacity 0 —
     browsers then starve or cancel that request, so the card is left with a
     broken image and an expanded reveal. Unblock after load + fonts + primary,
     and after the load-time height lock has committed. Timeouts keep one hung
     font or image from disabling hover forever. */
  const PAGE_SETTLE_TIMEOUT_MS = 5000;
  const PRIMARY_SETTLE_TIMEOUT_MS = 8000;

  const withTimeout = (promise, ms) =>
    Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);

  const afterNextPaint = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

  const whenImgSettled = (img) => {
    if (!img) return Promise.resolve();
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      // Load may have finished between the complete check and the listeners.
      if (img.complete) done();
    });
  };

  const pageSettled = withTimeout(
    Promise.all([
      document.readyState === 'complete'
        ? Promise.resolve()
        : new Promise((resolve) => window.addEventListener('load', resolve, { once: true })),
      document.fonts?.ready ?? Promise.resolve()
    ]).then(afterNextPaint),
    PAGE_SETTLE_TIMEOUT_MS
  );

  /* ---------------------------------------------------------------------------
     Mobile overlay reveal (badges + compare/wishlist)
     ---------------------------------------------------------------------------
     On mobile the labels and the action column sit permanently over the shot.
     They now fade in a beat after the page settles, and duck out again while the
     shopper is moving, so the image reads clean during the interaction. The scope
     of the duck-out follows the scope of the gesture:

       - Swiping ONE card's carousel ducks THAT card. Its neighbours are not part
         of the gesture, so they keep their badges.
       - Scrolling the page ducks every card, since the gesture belongs to no
         single card.

     ONE capture-phase scroll listener serves both. Scroll events do not bubble,
     but they do propagate through the capture phase, so the window's scroll and
     each [data-v2-carousel]'s scroll arrive here without a listener per card;
     event.target tells the two apart (`document` for the page, the carousel
     element for a swipe).

     The page-level class is namespaced to this component and sits on <body>
     (the theme's `.js` flag lives on <html>, so the CSS can keep `.js` as an
     ancestor). Per-card duck-out stays on the tile itself.
     --------------------------------------------------------------------------- */
  const OVERLAY_REVEAL_DELAY_MS = 1000;
  const OVERLAY_QUIET_RESUME_MS = 400;
  const OVERLAY_PAGE_QUIET_CLASS = 'product-tile-v2-page-quiet';

  let quietTimer = null;
  let quietActive = false;
  let quietBound = false;

  const endPageQuiet = () => {
    quietTimer = null;
    if (!quietActive) return;
    quietActive = false;
    document.body.classList.remove(OVERLAY_PAGE_QUIET_CLASS);
  };

  const onAnyScroll = (event) => {
    if (!mqMobile.matches) return;

    const { target } = event;
    if (target instanceof Element) {
      const carousel = target.closest('[data-v2-carousel]');
      if (carousel) {
        const tile = carousel.closest('product-tile-v2');
        if (tile instanceof ProductTileV2) tile._duckOverlays();
        return;
      }
      // Capture-phase scroll fires for every overflow box. Only the document /
      // html / body path is a page scroll; a filter drawer, etc. is not.
      if (target !== document.documentElement && target !== document.body) return;
    }

    if (!quietActive) {
      quietActive = true;
      document.body.classList.add(OVERLAY_PAGE_QUIET_CLASS);
    }
    clearTimeout(quietTimer);
    quietTimer = setTimeout(endPageQuiet, OVERLAY_QUIET_RESUME_MS);
  };

  const bindQuietScroll = () => {
    if (quietBound) return;
    quietBound = true;
    document.addEventListener('scroll', onAnyScroll, { capture: true, passive: true });
  };

  // Batch every tile's carousel measure into one frame: read all geometries,
  // then write all progress bars. Per-tile update() was write-then-read on
  // every custom-element upgrade (~20 cards) and showed up as forced reflow.
  const pendingCarouselProgress = new Map();
  let carouselProgressRaf = null;

  const scheduleCarouselProgress = (carousel, progressBar) => {
    if (!mqMobile.matches) return;
    pendingCarouselProgress.set(carousel, progressBar);
    if (carouselProgressRaf) return;
    carouselProgressRaf = requestAnimationFrame(() => {
      carouselProgressRaf = null;
      const reads = [];
      pendingCarouselProgress.forEach((bar, el) => {
        if (!el.isConnected) return;
        reads.push({
          progressBar: bar,
          scrollLeft: el.scrollLeft,
          scrollWidth: el.scrollWidth,
          clientWidth: el.clientWidth
        });
      });
      pendingCarouselProgress.clear();
      reads.forEach(({ progressBar, scrollLeft, scrollWidth, clientWidth }) => {
        if (scrollWidth <= clientWidth) return;
        progressBar.style.width = `${(clientWidth / scrollWidth) * 100}%`;
        progressBar.style.left = `${(Math.abs(scrollLeft) / scrollWidth) * 100}%`;
      });
    });
  };

  const relockHeights = () => {
    // Mobile: stacked card, no hover reveal — never touch inline heights.
    // Returning before the rAF avoids a write-then-read stampede on PLP load.
    if (!mqDesktop.matches) return;
    if (lockRaf) return;
    lockRaf = requestAnimationFrame(() => {
      lockRaf = null;
      if (!mqDesktop.matches) return;
      const tiles = [...lockedTiles].filter((t) => t.isConnected);
      if (!tiles.length) return;

      // Release locks so the grid re-stretches to the natural rest height.
      tiles.forEach((t) => {
        t.style.height = '';
      });

      // Force one reflow, then lock each tile to its stretched row height.
      void document.documentElement.offsetHeight;
      const heights = tiles.map((t) => t.offsetHeight);
      tiles.forEach((t, i) => {
        if (heights[i] > 0) t.style.height = `${heights[i]}px`;
      });
    });
  };

  // Drop the fixed heights immediately so the cards flow with the grid during a
  // live resize / filter animation instead of holding a stale height and snapping
  // only after the debounce. A released tile's natural (row-stretched) height
  // equals its locked height, so this is seamless — the lock is re-applied once
  // the layout settles (relockHeights) purely so the hover reveal can't reflow
  // the row. Cheap: clears inline styles, no forced reflow. AAS-5137.
  const releaseHeights = () => {
    lockedTiles.forEach((t) => {
      if (t.isConnected) t.style.height = '';
    });
  };

  let resizeTimer = null;
  const scheduleRelock = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(relockHeights, 150);
  };

  // Go fluid on the first resize event, re-pin 150ms after the last one.
  const onLayoutResize = () => {
    releaseHeights();
    scheduleRelock();
  };

  window.addEventListener('resize', onLayoutResize, { passive: true });
  window.addEventListener('load', relockHeights);
  mqDesktop.addEventListener('change', (event) => {
    if (event.matches) {
      lockedTiles.forEach((tile) => {
        if (!tile.isConnected) return;
        tile._initScrollers();
        observeGrid(tile.closest('.armada-product-grid'));
      });
      relockHeights();
      // Desktop hover owns the overlays from here; drop any duck-out state.
      clearTimeout(quietTimer);
      endPageQuiet();
      lockedTiles.forEach((tile) => tile._endQuiet());
    } else {
      lockedTiles.forEach((tile) => {
        if (!tile.isConnected) return;
        tile._initCarousel();
        tile._revealOverlays();
      });
      releaseHeights();
    }
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(relockHeights);
  }

  // The desktop filter opens as a right-side push panel that narrows the product
  // grid WITHOUT a window resize. Every tile then gets narrower, so its natural
  // (media-driven) height changes and the locked heights go stale — leaving each
  // card too tall and shoving the price/CTA down. Watch the grid width and relock
  // on change. We compare width only: our own locking changes the grid's HEIGHT,
  // so keying on width avoids a feedback loop. Grids are (re-)observed from each
  // tile's connectedCallback, so this survives facets.js AJAX re-renders. AAS-5137.
  let gridObserver = null;
  const observedGrids = new WeakSet();
  const observeGrid = (grid) => {
    if (!mqDesktop.matches) return;
    if (!grid || typeof ResizeObserver === 'undefined' || observedGrids.has(grid)) return;
    if (!gridObserver) {
      const gridWidths = new WeakMap();
      gridObserver = new ResizeObserver((entries) => {
        let widthChanged = false;
        entries.forEach((entry) => {
          const w = Math.round(entry.contentRect.width);
          if (gridWidths.get(entry.target) !== w) {
            gridWidths.set(entry.target, w);
            widthChanged = true;
          }
        });
        if (widthChanged) {
          releaseHeights(); // track the narrowing grid live, then re-pin on settle
          scheduleRelock();
        }
      });
    }
    observedGrids.add(grid);
    gridObserver.observe(grid);
  };

  class ProductTileV2 extends HTMLElement {
    constructor() {
      super();
      this._selectedColor = null;
      this._selectedSize = null;
      this._resizeObservers = [];
      this._pendingMedia = null;
      this._mediaSwapRunning = false;
      this._swapAbort = null;
      this._teardown = null;
      this._mediaPrefetched = false;
      this._overlaysRevealing = false;
      this._quietTimer = null;
      this._warmedImages = new Map();
      this._onWarmMedia = () => this._prefetchColourMedia();
    }

    connectedCallback() {
      this._teardown?.abort();
      this._teardown = new AbortController();
      const { signal } = this._teardown;

      this._initElements();
      this._guardRenderedSecondary();
      this._initCarousel();
      this._revealOverlays();
      this._initSwatches(signal);
      this._initSizes(signal);
      this._initCartButton();
      this._initScrollers();
      this._updateSizeAvailability();
      this.addEventListener('pointerenter', this._onWarmMedia, { signal });
      this.addEventListener('focusin', this._onWarmMedia, { signal });
      this._armHoverWhenReady(signal);

      lockedTiles.add(this);
      observeGrid(this.closest('.armada-product-grid'));
      relockHeights();
    }

    disconnectedCallback() {
      this._teardown?.abort();
      this._swapAbort?.abort();
      clearTimeout(this._quietTimer);
      this._overlaysRevealing = false;
      this._resizeObservers.forEach((ro) => ro.disconnect());
      this._resizeObservers = [];
      lockedTiles.delete(this);
      relockHeights();
    }

    // -------------------------------------------------------------------------
    // Element queries
    // -------------------------------------------------------------------------
    _initElements() {
      this.primaryImg = this.querySelector('.product-tile-v2__img--primary');
      this.secondaryImg = this.querySelector('.product-tile-v2__img--secondary');
      this.tileLinks = Array.from(this.querySelectorAll('[data-tile-link]'));
      this.swatchBtns = Array.from(this.querySelectorAll('.product-tile-v2__swatch'));
      this.sizeBtns = Array.from(this.querySelectorAll('.product-tile-v2__size-btn'));

      this.cartForm = this.querySelector('product-form form');
      this.cartFormHiddenId = this.querySelector('.product-variant-id');
      this.cartBtn = this.querySelector('.product-tile-v2__cart-btn');
      this.colorOptionIdx = parseInt(this.dataset.colorOptionIndex, 10);
      this.sizeOptionIdx = parseInt(this.dataset.sizeOptionIndex, 10);
      this.colorRequired = this.dataset.colorRequired === 'true';
      this.sizeRequired = this.dataset.sizeRequired === 'true';

      const variantsScript = this.querySelector('[data-variants-json]');
      try {
        this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
      } catch (_e) {
        this.variants = [];
      }

      this._syncSelectionFromDom();
    }

    /* Shared with the other tile — see assets/tile-shared.js. */
    _syncSelectionFromDom() {
      window.ArmadaTile?.syncSelectionFromDom(this);
    }

    _initCartButton() {
      window.ArmadaTile?.initCartButton(this);
    }

    _findMatchingVariants() {
      return window.ArmadaTile?.findMatchingVariants(this) || [];
    }

    // -------------------------------------------------------------------------
    // Mobile image carousel — progress bar follows native scroll-snap
    // (desktop has no scroll, so the bar stays hidden via CSS)
    // -------------------------------------------------------------------------
    _initCarousel() {
      if (!mqMobile.matches || this._carouselReady) return;
      const carousel = this.querySelector('[data-v2-carousel]');
      const progressBar = this.querySelector('[data-v2-progress-bar]');
      if (!carousel || !progressBar) return;
      this._carouselReady = true;

      const update = () => scheduleCarouselProgress(carousel, progressBar);

      carousel.addEventListener('scroll', update, { passive: true });
      const ro = new ResizeObserver(update);
      ro.observe(carousel);
      this._resizeObservers.push(ro);
    }

    /**
     * Fade the badges + compare/wishlist in a beat after the page settles
     * (mobile only — desktop gates the actions on hover and keeps the labels).
     * Every connected tile uses the same delay so they appear together. A card
     * injected later by facets / load-more still waits out the delay from
     * pageSettled (already resolved) plus 1s, rather than popping in at full
     * opacity. Disconnecting mid-delay clears _overlaysRevealing so a later
     * reconnect can schedule again.
     */
    _revealOverlays() {
      if (!mqMobile.matches) return;
      bindQuietScroll();
      if (this.classList.contains('product-tile-v2--overlays-in')) return;
      if (this._overlaysRevealing) return;
      this._overlaysRevealing = true;

      const { signal } = this._teardown ?? {};
      const revealDelay = mqReduceMotion.matches ? 0 : OVERLAY_REVEAL_DELAY_MS;
      pageSettled
        .then(() => new Promise((resolve) => setTimeout(resolve, revealDelay)))
        .then(() => {
          if (signal?.aborted || !this.isConnected) return;
          this.classList.add('product-tile-v2--overlays-in');
        });
    }

    /**
     * Duck THIS card's overlays for the length of a carousel swipe. Momentum
     * keeps firing scroll, so each event pushes the resume out; the badges come
     * back once the card has been still for OVERLAY_QUIET_RESUME_MS.
     */
    _duckOverlays() {
      this.classList.add('product-tile-v2--quiet');
      clearTimeout(this._quietTimer);
      this._quietTimer = setTimeout(() => {
        this._quietTimer = null;
        this.classList.remove('product-tile-v2--quiet');
      }, OVERLAY_QUIET_RESUME_MS);
    }

    _endQuiet() {
      clearTimeout(this._quietTimer);
      this._quietTimer = null;
      this.classList.remove('product-tile-v2--quiet');
    }

    // -------------------------------------------------------------------------
    // Swatch interactions — select colour + swap the card image pair
    // -------------------------------------------------------------------------
    _initSwatches(signal) {
      this.swatchBtns.forEach((btn) => {
        btn.addEventListener('click', () => this._handleSwatchClick(btn), { signal });
        btn.addEventListener(
          'keydown',
          (event) => {
            this._handleRovingKeys(event, this.swatchBtns, btn, () => this._handleSwatchClick(btn));
          },
          { signal }
        );
      });
      const selected =
        this.swatchBtns.find((btn) => btn.getAttribute('aria-checked') === 'true') || this.swatchBtns[0];
      if (selected) this._syncRovingTab(this.swatchBtns, selected);
    }

    _handleSwatchClick(btn) {
      this.swatchBtns.forEach((other) => {
        other.classList.remove('is-selected');
        other.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-checked', 'true');
      this._syncRovingTab(this.swatchBtns, btn);

      this._selectedColor = btn.dataset.swatchValue;
      this._swapMediaTo(btn);
      this._updateSizeAvailability();
      // Must follow _updateSizeAvailability: it can invalidate the selected
      // size, which changes the variant the price resolves to.
      this._updatePrice();
      this._syncWishlistVariant();

      if (btn.dataset.variantUrl) {
        this.tileLinks.forEach((link) => {
          link.href = btn.dataset.variantUrl;
        });
      }
    }

    _mediaFromButton(btn) {
      // AAS-5490: on >=2dppx screens prefer the harder-compressed hd pair —
      // artefacts optically halve there, so the hd variant looks identical at
      // a fraction of the bytes. Resolved here so the rest of the swap
      // pipeline (warming included) never sees more than one variant.
      const d = btn.dataset;
      const hd = ProductTileV2._isHiDpi();
      return {
        primarySrc: (hd && d.primarySrcHd) || d.primarySrc,
        primarySrcset: (hd && d.primarySrcsetHd) || d.primarySrcset,
        secondarySrc: (hd && d.secondarySrcHd) || d.secondarySrc,
        secondarySrcset: (hd && d.secondarySrcsetHd) || d.secondarySrcset,
      };
    }

    static _isHiDpi() {
      // AAS-5559: one home for the density query. picture-swap.js caches the
      // MediaQueryList and reads .matches live, so a window moved between
      // monitors of different DPR still answers correctly — and the two other
      // components that asked this stop rebuilding an MQL on every click.
      return window.armadaIsHiDpi?.() || false;
    }

    _beginSwap() {
      this._swapAbort?.abort();
      this._swapAbort = new AbortController();
      return this._swapAbort.signal;
    }

    _isActiveSwap(signal) {
      return Boolean(signal) && !signal.aborted && this._swapAbort?.signal === signal;
    }

    _whenAborted(signal) {
      return new Promise((resolve) => {
        if (!signal) return;
        if (signal.aborted) {
          resolve();
          return;
        }
        signal.addEventListener('abort', () => resolve(), { once: true });
      });
    }

    // Latest click wins. An in-flight fade is aborted so it cannot block or
    // clobber the colour the user actually landed on.
    _swapMediaTo(btn) {
      const media = this._mediaFromButton(btn);
      if (!media.primarySrc || !this.primaryImg) return;

      const signal = this._beginSwap();

      // Same-image fast path. Before the first swap the img's src attribute is
      // the Liquid-rendered URL even when a hi-DPI <source> painted the hd
      // variant — compare against both so the initially selected colour still
      // short-circuits on >=2dppx screens.
      const currentSrc = this.primaryImg.getAttribute('src');
      if (currentSrc === media.primarySrc || currentSrc === btn.dataset.primarySrc) {
        this._pendingMedia = null;
        this._resetFadeLayer();
        this._commitMedia(media);
        return;
      }

      this._pendingMedia = media;
      this._drainMediaSwaps(signal);
    }

    async _drainMediaSwaps(signal) {
      if (this._mediaSwapRunning) return;
      this._mediaSwapRunning = true;
      try {
        while (this._pendingMedia && this._isActiveSwap(signal)) {
          const media = this._pendingMedia;
          this._pendingMedia = null;
          await this._performMediaSwap(media, signal);
        }
      } finally {
        this._mediaSwapRunning = false;
      }
      if (this._pendingMedia && this._swapAbort && !this._swapAbort.signal.aborted) {
        this._drainMediaSwaps(this._swapAbort.signal);
      }
    }

    async _performMediaSwap(media, signal) {
      if (!this._isActiveSwap(signal)) return;

      const instant = mqReduceMotion.matches || mqMobile.matches;

      // Fade only when prefetch already decoded the file. Never wait on the
      // network — that was the 3–4s freeze on 2000px DAM masters.
      if (!instant && this._primaryIsCached(media)) {
        await this._crossfadeTo(media, signal);
        return;
      }

      if (!this._isActiveSwap(signal)) return;
      this._resetFadeLayer();
      this._commitMedia(media);
    }

    _armHoverWhenReady(signal) {
      // Only the primary must be ready. The secondary is opacity:0 + lazy, so
      // waiting on it can leave hover blocked forever.
      Promise.all([pageSettled, withTimeout(whenImgSettled(this.primaryImg), PRIMARY_SETTLE_TIMEOUT_MS)]).then(() => {
        if (signal?.aborted || !this.isConnected) return;
        this.classList.remove('product-tile-v2--hover-blocked');
        this.removeAttribute('aria-busy');
        if (this.matches(':hover, :focus-within')) this._prefetchColourMedia();
      });
    }

    _prefetchColourMedia() {
      if (this.classList.contains('product-tile-v2--hover-blocked')) return;
      if (this._mediaPrefetched) return;
      if (!mqDesktop.matches) return;
      this._mediaPrefetched = true;

      this.swatchBtns.forEach((btn) => {
        const media = this._mediaFromButton(btn);
        this._warmImage(media.primarySrc, media.primarySrcset);
        this._warmImage(media.secondarySrc, media.secondarySrcset);
      });
    }

    _applyImageSource(image, src, srcset) {
      /* AAS-5559: this used to REMOVE the picture's sources. That repainted
       * correctly but cost the retina split for the rest of the session —
       * every later paint served full quality. The sources are updated
       * instead, so the gated one stays gated and the split survives the
       * swap. See assets/picture-swap.js. */
      if (this.primaryImg?.sizes) image.sizes = this.primaryImg.sizes;
      /* Both sources take the same ladder here, deliberately: _mediaFromButton
       * has already picked the hd pair by matchMedia, so `srcset` IS the right
       * variant for this device. The gated source then matches on hi-DPI and
       * serves the retina ladder; on DPR1 it never matches and the ungated one
       * answers with the standard ladder. The split holds either way — only a
       * DPR change without a reload could desync it, which the overlay already
       * reports as a kept candidate. */
      window.armadaPaintPicture?.(image, {
        src: src,
        srcset: srcset || '',
        srcsetRetina: srcset || '',
      });
    }

    _warmKey(src, srcset) {
      return `${src}\n${srcset || ''}`;
    }

    _warmImage(src, srcset) {
      if (!src) return null;
      const key = this._warmKey(src, srcset);
      const cached = this._warmedImages.get(key);
      if (cached) return cached;

      const image = new Image();
      this._applyImageSource(image, src, srcset);
      this._warmedImages.set(key, image);
      return image;
    }

    _primaryIsCached(media) {
      const image = this._warmedImages.get(this._warmKey(media.primarySrc, media.primarySrcset));
      return Boolean(image?.complete && image.naturalWidth > 0);
    }

    async _crossfadeTo(media, signal) {
      const fadeImg = this._ensureFadeLayer();
      this._applyImageSource(fadeImg, media.primarySrc, media.primarySrcset);
      await this._safeDecode(fadeImg, signal);
      if (!this._isActiveSwap(signal)) return;

      fadeImg.classList.remove('is-instant');
      await this._nextFrame(signal);
      if (!this._isActiveSwap(signal)) return;

      fadeImg.classList.add('is-visible');
      await this._waitForFade(fadeImg, signal);
      if (!this._isActiveSwap(signal)) return;

      this.classList.add('product-tile-v2--media-instant');
      this._commitMedia(media);
      await this._safeDecode(this.primaryImg, signal);
      if (!this._isActiveSwap(signal)) return;

      fadeImg.classList.add('is-instant');
      fadeImg.classList.remove('is-visible');
      void fadeImg.offsetWidth;
      this.classList.remove('product-tile-v2--media-instant');
      await this._nextFrame(signal);
      if (!this._isActiveSwap(signal)) return;
      fadeImg.classList.remove('is-instant');
    }

    _resetFadeLayer() {
      const fadeImg = this.fadeImg;
      if (!fadeImg) return;
      fadeImg.classList.add('is-instant');
      fadeImg.classList.remove('is-visible');
      void fadeImg.offsetWidth;
      fadeImg.classList.remove('is-instant');
      this.classList.remove('product-tile-v2--media-instant');
    }

    _commitMedia({ primarySrc, primarySrcset, secondarySrc, secondarySrcset }) {
      this._applyImageSource(this.primaryImg, primarySrc, primarySrcset);

      if (!this.secondaryImg) return;

      // Never use another colour's product shot as this colour's hover image.
      const allowSecondary =
        Boolean(secondarySrc) && !this._isOtherColorImage(primarySrc, secondarySrc);

      if (allowSecondary) {
        // The hover slide sits in its own <picture> with its own quality
        // <source>s — collapse it the same way as the primary, or a >=2dppx
        // screen keeps painting the original colour's hover shot.
        this._applyImageSource(this.secondaryImg, secondarySrc, secondarySrcset || '');
        this.classList.remove('product-tile-v2--no-swap');
      } else {
        this.classList.add('product-tile-v2--no-swap');
      }
    }

    /**
     * Last line of defence for Liquid-rendered markup: if the hover slide is
     * another colour's main shot, disable the swap rather than fade to the wrong
     * colour. Reads raw attributes so protocol-relative CDN URLs still match.
     */
    _guardRenderedSecondary() {
      if (!this.secondaryImg || !this.primaryImg || !this.swatchBtns.length) return;

      const primarySrc = this.primaryImg.getAttribute('src');
      const secondarySrc = this.secondaryImg.getAttribute('src');
      if (this._isOtherColorImage(primarySrc, secondarySrc)) {
        this.classList.add('product-tile-v2--no-swap');
      }
    }

    _isOtherColorImage(primarySrc, secondarySrc) {
      const primary = this._normalizeSrc(primarySrc);
      const secondary = this._normalizeSrc(secondarySrc);
      if (!secondary || secondary === primary) return true;

      return this.swatchBtns.some((btn) => {
        const swatchPrimary = this._normalizeSrc(btn.dataset.primarySrc);
        return swatchPrimary && swatchPrimary !== primary && swatchPrimary === secondary;
      });
    }

    // CDN URLs vary by protocol and cache-busting query but point at one asset.
    _normalizeSrc(src) {
      return src ? src.split('?')[0].replace(/^https?:/, '') : '';
    }

    _ensureFadeLayer() {
      if (this.fadeImg) return this.fadeImg;

      const fadeImg = document.createElement('img');
      fadeImg.className = 'product-tile-v2__img product-tile-v2__img--fade';
      fadeImg.alt = '';
      fadeImg.setAttribute('aria-hidden', 'true');
      fadeImg.decoding = 'async';
      fadeImg.draggable = false;
      fadeImg.sizes = this.primaryImg.sizes;
      this.querySelector('.product-tile-v2__media')?.append(fadeImg);
      this.fadeImg = fadeImg;
      return fadeImg;
    }

    async _safeDecode(img, signal) {
      if (!img?.decode) return;
      try {
        await Promise.race([img.decode(), this._whenAborted(signal), this._timeout(250)]);
      } catch (_error) {
        // decode() can reject for cached/unsupported images; load success is enough.
      }
    }

    _timeout(ms) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    }

    _nextFrame(signal) {
      return Promise.race([
        new Promise((resolve) => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            resolve();
          };
          requestAnimationFrame(() => requestAnimationFrame(finish));
          setTimeout(finish, 50);
        }),
        this._whenAborted(signal)
      ]);
    }

    _waitForFade(element, signal) {
      return new Promise((resolve) => {
        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          element.removeEventListener('transitionend', onEnd);
          signal?.removeEventListener('abort', finish);
          clearTimeout(timer);
          resolve();
        };
        const onEnd = (event) => {
          if (event.propertyName === 'opacity') finish();
        };
        element.addEventListener('transitionend', onEnd);
        signal?.addEventListener('abort', finish, { once: true });
        const duration = getComputedStyle(element).transitionDuration.split(',')[0].trim();
        const parsedDuration = Number.parseFloat(duration);
        const fadeMs = Number.isFinite(parsedDuration)
          ? parsedDuration * (duration.endsWith('ms') ? 1 : 1000)
          : 0;
        const timer = setTimeout(finish, fadeMs + 50);
      });
    }

    // -------------------------------------------------------------------------
    // Size interactions
    // -------------------------------------------------------------------------
    _initSizes(signal) {
      this.sizeBtns.forEach((btn) => {
        btn.addEventListener('click', () => this._handleSizeClick(btn), { signal });
        btn.addEventListener(
          'keydown',
          (event) => {
            this._handleRovingKeys(event, this.sizeBtns, btn, () => this._handleSizeClick(btn));
          },
          { signal }
        );
      });
      const selected =
        this.sizeBtns.find((btn) => btn.getAttribute('aria-checked') === 'true') || this.sizeBtns[0];
      if (selected) this._syncRovingTab(this.sizeBtns, selected);
    }

    _handleSizeClick(btn) {
      if (btn.disabled) return;
      this.sizeBtns.forEach((other) => {
        other.classList.remove('is-selected');
        other.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-checked', 'true');
      this._syncRovingTab(this.sizeBtns, btn);
      this._selectedSize = btn.dataset.sizeValue;
      this._updatePrice();
      this._syncWishlistVariant();
    }

    /**
     * Recompute size pill availability for the currently selected colour,
     * mirroring VariantSelects.updateVariantStatuses() on the PDP.
     * Clears an invalidated size selection so ATC falls through to quick-view
     * instead of silently adding a different size.
     */
    _updateSizeAvailability() {
      if (!this.sizeBtns.length || !this.variants.length) return;

      const { colorOptionIdx: colorIdx, sizeOptionIdx: sizeIdx, _selectedColor: selectedColor } = this;
      if (Number.isNaN(sizeIdx) || sizeIdx < 0) return;

      const soldOutLabel = window.variantStrings?.soldOut || 'Sold out';
      const colorKey = `option${colorIdx + 1}`;
      const sizeKey = `option${sizeIdx + 1}`;
      const hasColorFilter = !Number.isNaN(colorIdx) && colorIdx >= 0 && selectedColor;

      const matchesColor = (variant) => !hasColorFilter || variant[colorKey] === selectedColor;

      const availableSizes = new Set(
        this.variants.filter((v) => v.available && matchesColor(v)).map((v) => v[sizeKey])
      );

      let selectedStillAvailable = false;

      this.sizeBtns.forEach((btn) => {
        const { sizeValue } = btn.dataset;
        const available = availableSizes.has(sizeValue);
        const matchingVariant =
          this.variants.find((v) => v[sizeKey] === sizeValue && matchesColor(v) && v.available) ||
          this.variants.find((v) => v[sizeKey] === sizeValue && matchesColor(v));

        btn.disabled = !available;
        btn.classList.toggle('is-sold-out', !available);
        btn.setAttribute('aria-label', available ? sizeValue : `${sizeValue} — ${soldOutLabel}`);

        if (matchingVariant) {
          btn.dataset.variantId = matchingVariant.id;
        }

        if (!available && btn.getAttribute('aria-checked') === 'true') {
          btn.classList.remove('is-selected');
          btn.setAttribute('aria-checked', 'false');
        } else if (available && this._selectedSize === sizeValue) {
          selectedStillAvailable = true;
        }
      });

      if (this._selectedSize && !selectedStillAvailable) {
        this._selectedSize = null;
      }

      const tabTarget =
        this.sizeBtns.find((btn) => btn.getAttribute('aria-checked') === 'true' && !btn.disabled) ||
        this.sizeBtns.find((btn) => !btn.disabled) ||
        this.sizeBtns[0];
      if (tabTarget) this._syncRovingTab(this.sizeBtns, tabTarget);

      const focusedSize = document.activeElement;
      if (tabTarget && this.sizeBtns.includes(focusedSize) && focusedSize.disabled) {
        tabTarget.focus();
      }
    }

    _handleRovingKeys(event, buttons, current, activate) {
      const { key } = event;
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        activate();
      } else if (key === 'ArrowRight' || key === 'ArrowDown') {
        event.preventDefault();
        this._focusSibling(buttons, current, 1);
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        event.preventDefault();
        this._focusSibling(buttons, current, -1);
      }
    }

    _syncRovingTab(buttons, current) {
      buttons.forEach((btn) => {
        btn.tabIndex = btn === current ? 0 : -1;
      });
    }

    _focusSibling(buttons, current, direction) {
      const enabled = buttons.filter((btn) => !btn.disabled);
      const idx = enabled.indexOf(current);
      const next = enabled[idx + direction];
      if (!next) return;
      this._syncRovingTab(buttons, next);
      next.focus();
    }

    _handleCartClick(event) {
      if (!this.cartForm) return;

      // Single-variant product (no variants JSON): the hidden id already holds
      // the only variant id — let the form submit as-is.
      if (!this.variants.length) return;

      // Mobile: variant UI is hidden — always open quick-view for multi-variant
      // products so colour/size selection happens in the layer.
      const isMobile = mqMobile.matches;
      if (isMobile && (this.colorRequired || this.sizeRequired || this.variants.length > 1)) {
        event.preventDefault();
        this._openQuickView(this.dataset.productUrl);
        return;
      }

      this._syncSelectionFromDom();

      const hasUnselectedColor = this.colorOptionIdx >= 0 && this.colorRequired && !this._selectedColor;
      const hasUnselectedSize = this.sizeOptionIdx >= 0 && this.sizeRequired && !this._selectedSize;
      if (hasUnselectedColor || hasUnselectedSize) {
        event.preventDefault();
        this._openQuickView(this.dataset.productUrl);
        return;
      }

      const matches = this._findMatchingVariants();
      if (matches.length === 0) {
        event.preventDefault();
        this._openQuickView(this.dataset.productUrl);
        return;
      }

      if (this.cartFormHiddenId) {
        this.cartFormHiddenId.value = matches[0].id;
      }
    }

    /* Price resolution + repaint live in assets/tile-shared.js, shared with the
       v1 <product-tile> — both elements expose the same variants/selection
       contract, so neither needs its own copy. */
    _updatePrice() {
      window.ArmadaTile?.updatePrice(this);
    }

    /* AAS-5630: the wishlist saves whatever the toggle points at, so it has to
       follow the same variant the card shows and prices. */
    _syncWishlistVariant() {
      const variant = window.ArmadaTile?.resolveDisplayVariant(this);
      if (variant) window.armadaSyncWishlistVariant?.(this, variant.id);
    }

    // -------------------------------------------------------------------------
    // Scrollable colour / size rows — gradient arrows shown per available
    // direction, toggled on overflow
    // -------------------------------------------------------------------------
    _initScrollers() {
      // Colour/size rows are `display: none` below 990px — measuring them on
      // every PLP tile forces a reflow for no visible UI.
      if (!mqDesktop.matches || this._scrollersReady) return;
      this._scrollersReady = true;

      this.querySelectorAll('[data-v2-scroller]').forEach((scroller) => {
        const track = scroller.querySelector('[data-v2-scroller-track]');
        const btnPrev = scroller.querySelector('[data-v2-scroll-prev]');
        const btnNext = scroller.querySelector('[data-v2-scroll-next]');
        if (!track || !btnPrev || !btnNext) return;

        const update = () => {
          const maxScroll = track.scrollWidth - track.clientWidth;
          if (maxScroll <= 1) {
            btnPrev.hidden = true;
            btnNext.hidden = true;
            return;
          }
          const pos = Math.abs(track.scrollLeft);
          btnPrev.hidden = pos <= 1;
          btnNext.hidden = pos >= maxScroll - 1;
        };

        const scrollByDir = (direction) => {
          const reduceMotion = mqReduceMotion.matches;
          track.scrollBy({
            left: direction * track.clientWidth * 0.8,
            behavior: reduceMotion ? 'auto' : 'smooth'
          });
        };

        btnPrev.addEventListener('click', () => scrollByDir(-1));
        btnNext.addEventListener('click', () => scrollByDir(1));
        track.addEventListener('scroll', update, { passive: true });

        // Re-measure when the row gains layout (hover reveal, fonts, column width)
        const ro = new ResizeObserver(update);
        ro.observe(track);
        this._resizeObservers.push(ro);

        update();
      });
    }

    _openQuickView(productUrl) {
      if (!productUrl) return;
      const qv = this.querySelector('.product-tile-v2__quick-view-trigger') || this.querySelector('quick-view');
      if (qv) {
        const prev = qv.dataset.productUrl;
        qv.dataset.productUrl = productUrl;
        qv.click();
        qv.dataset.productUrl = prev;
      } else {
        document.dispatchEvent(
          new CustomEvent('product-tile:open-quick-view', {
            bubbles: true,
            detail: { productUrl, tile: this }
          })
        );
      }
    }
  }

  customElements.define('product-tile-v2', ProductTileV2);
}
